/**
 * Seletores desenhados pelo design system: lista de opções e calendário.
 *
 * O `<select>` nativo e o `<input type="date">` abrem o menu do sistema
 * operacional, que não segue tema, fonte, cor nem tamanho do Suite. Aqui a
 * lista e o calendário são desenhados pelo DS, e o controle nativo continua
 * no documento, invisível, para que o formulário HTML continue funcionando:
 * `name` entra no `FormData`, `required` bloqueia o `submit`, `form` liga a um
 * formulário fora da árvore, e o `onChange` do consumidor recebe um evento
 * com `target.value` igual ao que receberia do controle nativo.
 *
 * COMO O EVENTO CHEGA IGUAL
 *
 *   O valor é escrito no elemento nativo escondido pelo setter do protótipo
 *   (`HTMLSelectElement.prototype.value`), e não pela propriedade do nó.
 *   React guarda um rastreador de valor no nó para deduplicar eventos; o
 *   setter do protótipo passa por fora dele, então o `input`/`change` que
 *   disparamos em seguida é entregue ao `onChange` do consumidor como se a
 *   pessoa tivesse mexido no controle nativo. `e.target.value`,
 *   `e.target.name`, `e.target.form`, `e.target.selectedOptions` funcionam.
 *
 * DATA E HORA SEM FUSO
 *
 *   O valor de `CampoDeData` é a string ISO local do tipo (`2026-09-13`,
 *   `2026-09-13T14:30`, `2026-09`, `14:30`), exatamente o que o `<input>`
 *   nativo devolve. Nada passa por `new Date()` na saída: os pedaços são
 *   montados com `padStart`, então não há conversão de fuso.
 */

import {
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  Children,
  isValidElement,
  type CSSProperties,
  type FocusEvent as ReactFocusEvent,
  type InputHTMLAttributes,
  type KeyboardEvent as ReactKeyboardEvent,
  type RefObject,
  type ReactElement,
  type ReactNode,
  type SelectHTMLAttributes,
} from 'react';
import { createPortal } from 'react-dom';
import { Icone } from './icones';
import { empilharEsc } from './pilha-do-esc';
import './seletores.css';

/* ---------------- Portal flutuante (comum aos dois) ---------------- */

type Posicao = { top: number; left: number; width: number; flip: boolean; maxHeight: number };

/**
 * Mesma decisão do `SelectField` de `Modal.tsx`: o painel é um portal com
 * `position:fixed`, para não entrar na área rolável de um `.modal` e "criar
 * scroll" ali. Vira para cima quando não cabe embaixo. Fecha ao rolar
 * qualquer coisa (captura), porque um painel fixo desalinha do campo.
 */
function usePainelFlutuante(
  aberto: boolean,
  ancora: RefObject<HTMLElement | null>,
  alturaPrevista: number,
  larguraMinima = 0,
) {
  const [pos, setPos] = useState<Posicao | null>(null);
  useLayoutEffect(() => {
    if (!aberto || !ancora.current) { setPos(null); return; }
    function calcular() {
      const r = ancora.current!.getBoundingClientRect();
      /* O chão é o topo da pílula de navegação, quando ela está na tela: abrir
         para baixo até o fim da janela deixava as últimas opções atrás dela
         (achado da F4, no fim das Configurações). */
      const pilula = document.querySelector('.navegacao-suite')?.getBoundingClientRect();
      const chao = pilula && pilula.height > 0 ? Math.min(window.innerHeight, pilula.top - 8) : window.innerHeight;
      const espacoEmbaixo = chao - r.bottom;
      const flip = espacoEmbaixo < alturaPrevista + 12 && r.top > espacoEmbaixo;
      const width = Math.min(window.innerWidth - 16, Math.max(r.width, larguraMinima));
      const left = Math.max(8, Math.min(r.left, window.innerWidth - width - 8));
      setPos({ top: flip ? r.top - 6 : r.bottom + 6, left, width, flip, maxHeight: Math.max(120, (flip ? r.top : chao - r.bottom) - 14) });
    }
    calcular();
    window.addEventListener('resize', calcular);
    return () => window.removeEventListener('resize', calcular);
  }, [aberto, ancora, alturaPrevista, larguraMinima]);
  return pos;
}

function estiloDoPainel(pos: Posicao): CSSProperties {
  return {
    position: 'fixed',
    left: pos.left,
    width: pos.width,
    maxHeight: pos.maxHeight,
    ...(pos.flip ? { bottom: window.innerHeight - pos.top } : { top: pos.top }),
  };
}

/**
 * Clique fora, rolagem e Esc fecham. O Esc passa pela pilha de `pilha-do-esc`
 * (a mesma do modal), que ouve na captura do `document`: sem isso, o modal que
 * contém o campo recebia o Esc primeiro e fechava junto com a lista. O
 * primeiro ref é sempre o botão que abriu, e o foco volta para ele.
 */
function useFecharFora(aberto: boolean, refs: RefObject<HTMLElement | null>[], fechar: () => void) {
  useEffect(() => {
    if (!aberto) return;
    // Um painel do DS aberto por cima deste (o seletor de ano dentro do
    // calendário é um `SelectDoSuite` com portal próprio) não conta como
    // "fora": clicar ou rolar nele não fecha o painel de baixo.
    const dentroDeAlgumPainel = (alvo: EventTarget | null) =>
      alvo instanceof Element && alvo.closest('.seletor__lista') !== null;
    function aoApontar(e: PointerEvent) {
      const alvo = e.target as Node;
      if (refs.some((r) => r.current?.contains(alvo))) return;
      if (dentroDeAlgumPainel(e.target)) return;
      fechar();
    }
    function aoRolar(e: Event) {
      // Rolagem dentro do próprio painel (lista longa) não fecha.
      if (refs.some((r) => r.current?.contains(e.target as Node))) return;
      if (dentroDeAlgumPainel(e.target)) return;
      fechar();
    }
    const desempilhar = empilharEsc(() => {
      fechar();
      refs[0]?.current?.focus();
    });
    document.addEventListener('pointerdown', aoApontar);
    document.addEventListener('scroll', aoRolar, true);
    return () => {
      desempilhar();
      document.removeEventListener('pointerdown', aoApontar);
      document.removeEventListener('scroll', aoRolar, true);
    };
  }, [aberto, refs, fechar]);
}

/**
 * Escreve no controle nativo escondido e dispara `input` + `change` para que
 * o `onChange` do consumidor (React) e qualquer ouvinte nativo recebam o
 * evento com `target` sendo o elemento de formulário de verdade.
 */
function escreverNoNativo(el: HTMLSelectElement | HTMLInputElement, valor: string) {
  const proto = el instanceof HTMLSelectElement ? HTMLSelectElement.prototype : HTMLInputElement.prototype;
  const setter = Object.getOwnPropertyDescriptor(proto, 'value')?.set;
  if (setter) setter.call(el, valor); else el.value = valor;
  el.dispatchEvent(new Event('input', { bubbles: true }));
  el.dispatchEvent(new Event('change', { bubbles: true }));
}

function escreverMultiplo(el: HTMLSelectElement, valores: string[]) {
  const escolhidos = new Set(valores);
  for (const o of Array.from(el.options)) {
    const setter = Object.getOwnPropertyDescriptor(HTMLOptionElement.prototype, 'selected')?.set;
    if (setter) setter.call(o, escolhidos.has(o.value)); else o.selected = escolhidos.has(o.value);
  }
  el.dispatchEvent(new Event('input', { bubbles: true }));
  el.dispatchEvent(new Event('change', { bubbles: true }));
}

/* ======================================================================
 * SelectDoSuite
 * ====================================================================== */

type OpcaoLida = {
  value: string;
  rotulo: string;
  disabled: boolean;
  grupo?: string;
  /** Índice dentro da lista plana de opções, contando as desabilitadas. */
  i: number;
};

/**
 * Lê `<option>` e `<optgroup>` dos children, do mesmo jeito que o `<select>`
 * nativo faria. O `value` cai no texto quando não é informado, como no HTML.
 */
function lerOpcoes(children: ReactNode): OpcaoLida[] {
  const lista: OpcaoLida[] = [];
  function texto(n: ReactNode): string {
    if (n == null || typeof n === 'boolean') return '';
    if (typeof n === 'string' || typeof n === 'number') return String(n);
    if (Array.isArray(n)) return n.map(texto).join('');
    if (isValidElement<{ children?: ReactNode }>(n)) return texto(n.props.children);
    return '';
  }
  function andar(n: ReactNode, grupo?: string, grupoDesabilitado = false) {
    Children.forEach(n, (filho) => {
      if (!isValidElement(filho)) return;
      const el = filho as ReactElement<Record<string, unknown>>;
      if (el.type === 'optgroup') {
        andar(el.props.children as ReactNode, String(el.props.label ?? ''), Boolean(el.props.disabled));
        return;
      }
      if (el.type === 'option') {
        const rotulo = texto(el.props.children as ReactNode);
        const value = el.props.value == null ? rotulo : String(el.props.value);
        lista.push({ value, rotulo, disabled: grupoDesabilitado || Boolean(el.props.disabled), grupo, i: lista.length });
        return;
      }
      // Fragment ou qualquer embrulho: desce.
      if (el.props.children) andar(el.props.children as ReactNode, grupo, grupoDesabilitado);
    });
  }
  andar(children);
  return lista;
}

export type SelectDoSuiteProps = SelectHTMLAttributes<HTMLSelectElement> & {
  /** Rótulo lido pelo leitor de tela quando não há `<label for>` ligado. */
  'aria-label'?: string;
  /** Texto quando nada está escolhido (só faz sentido com `multiple` ou sem opção com valor vazio). */
  placeholder?: string;
};

/**
 * Lista de opções com a API do `<select>`: mesmas props, mesmos children,
 * mesmo evento. Nunca abre o menu nativo.
 *
 * Teclado no botão: setas, Enter, Espaço, Alt+seta abrem; setas com a lista
 * fechada trocam o valor direto (como o `<select>` faz). Na lista: setas,
 * Home, End, PageUp/PageDown, Enter/Espaço escolhem, Esc fecha e devolve o
 * foco, digitar salta para a opção que começa com as letras (typeahead).
 */
export function SelectDoSuite(props: SelectDoSuiteProps) {
  const {
    children, id, className = '', value, defaultValue, onChange, disabled, multiple, placeholder,
    'aria-label': ariaLabel, 'aria-labelledby': ariaLabelledby, 'aria-describedby': ariaDescribedby,
    'aria-invalid': ariaInvalid, onBlur, onFocus, style, ...resto
  } = props;
  const gerado = useId();
  const meuId = id ?? gerado;
  const opcoes = useMemo(() => lerOpcoes(children), [children]);

  const nativoRef = useRef<HTMLSelectElement>(null);
  const botaoRef = useRef<HTMLButtonElement>(null);
  const listaRef = useRef<HTMLDivElement>(null);
  const refs = useMemo(() => [botaoRef, listaRef], []);

  /* Valor: controlado se `value` veio; senão, estado interno a partir de `defaultValue`. */
  const controlado = value !== undefined;
  const paraLista = (v: unknown): string[] =>
    v == null ? [] : Array.isArray(v) ? v.map(String) : [String(v)];
  const [interno, setInterno] = useState<string[]>(() => {
    if (defaultValue !== undefined) return paraLista(defaultValue);
    // Sem valor inicial o `<select>` nativo escolhe a primeira opção habilitada.
    const primeira = opcoes.find((o) => !o.disabled);
    return !multiple && primeira ? [primeira.value] : [];
  });
  const escolhidos = controlado ? paraLista(value) : interno;
  useEffect(() => {
    const el = nativoRef.current;
    const form = el?.form;
    if (!el || !form) return;
    const restaurar = () => queueMicrotask(() => {
      const novos = controlado ? paraLista(value) : defaultValue !== undefined ? paraLista(defaultValue) : multiple ? [] : [opcoes.find(o => !o.disabled)?.value ?? ''];
      if (!controlado) setInterno(novos);
      for (const o of Array.from(el.options)) o.selected = novos.includes(o.value);
    });
    form.addEventListener('reset', restaurar);
    return () => form.removeEventListener('reset', restaurar);
  }, [controlado, value, defaultValue, multiple, opcoes]);


  const [aberto, setAberto] = useState(false);
  const [ativo, setAtivo] = useState(0);
  /* Nome da lista para o leitor de tela: o `aria-label` recebido ou o texto
     do `<label for>` do consumidor, lido do documento na hora de abrir. */
  const [nomeDaLista, setNomeDaLista] = useState<string | undefined>(undefined);
  const pos = usePainelFlutuante(aberto, botaoRef, Math.min(288, opcoes.length * 37 + 8));

  const fechar = useMemo(() => () => setAberto(false), []);
  useFecharFora(aberto, refs, fechar);

  function abrir() {
    if (disabled) return;
    const primeiroEscolhido = opcoes.findIndex((o) => escolhidos.includes(o.value) && !o.disabled);
    const primeiroHabilitado = opcoes.findIndex((o) => !o.disabled);
    setAtivo(primeiroEscolhido >= 0 ? primeiroEscolhido : Math.max(0, primeiroHabilitado));
    if (!ariaLabel && !ariaLabelledby) {
      const rotulo = document.querySelector<HTMLLabelElement>(`label[for="${CSS.escape(meuId)}"]`);
      setNomeDaLista(rotulo?.textContent?.trim() || undefined);
    }
    setAberto(true);
  }

  /* `pos` entra nas dependências de propósito: a lista só existe no DOM
     depois que o layout effect calcula a posição, um commit depois de
     `aberto` virar true. Sem `pos` aqui, o foco iria para um `ref` nulo. */
  useEffect(() => {
    if (!aberto || !pos) return;
    listaRef.current?.focus();
  }, [aberto, pos]);

  useEffect(() => {
    if (!aberto || !pos) return;
    listaRef.current?.querySelector<HTMLElement>(`[data-i="${ativo}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [aberto, pos, ativo]);

  function aplicar(novos: string[]) {
    if (!controlado) setInterno(novos);
    const el = nativoRef.current;
    if (!el) return;
    if (multiple) escreverMultiplo(el, novos);
    else escreverNoNativo(el, novos[0] ?? '');
  }

  function escolher(o: OpcaoLida) {
    if (o.disabled) return;
    if (multiple) {
      const novos = escolhidos.includes(o.value)
        ? escolhidos.filter((v) => v !== o.value)
        : opcoes.filter((x) => escolhidos.includes(x.value) || x.value === o.value).map((x) => x.value);
      aplicar(novos);
      return; // com múltipla escolha a lista fica aberta
    }
    if (escolhidos[0] !== o.value) aplicar([o.value]);
    setAberto(false);
    botaoRef.current?.focus();
  }

  function proximoHabilitado(de: number, passo: 1 | -1): number {
    let i = de;
    for (let n = 0; n < opcoes.length; n++) {
      i = Math.min(opcoes.length - 1, Math.max(0, i + passo));
      if (!opcoes[i]?.disabled) return i;
      if (i === 0 || i === opcoes.length - 1) break;
    }
    return de;
  }
  const primeiroHabilitado = () => opcoes.findIndex((o) => !o.disabled);
  const ultimoHabilitado = () => { for (let i = opcoes.length - 1; i >= 0; i--) if (!opcoes[i]?.disabled) return i; return -1; };

  /* Typeahead: letras digitadas em sequência curta formam um prefixo. */
  const digitado = useRef({ texto: '', quando: 0 });
  function saltarPorTexto(tecla: string, aPartirDe: number): number {
    const agora = Date.now();
    const seguido = agora - digitado.current.quando < 700;
    const texto = (seguido ? digitado.current.texto : '') + tecla.toLowerCase();
    digitado.current = { texto, quando: agora };
    const normal = (s: string) => s.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
    const alvo = normal(texto);
    // Mesma letra repetida cicla entre as opções que começam com ela.
    const cicla = alvo.length > 1 && alvo.split('').every((c) => c === alvo[0]);
    const prefixo = cicla ? alvo[0]! : alvo;
    const inicio = cicla || !seguido ? aPartirDe + 1 : aPartirDe;
    for (let n = 0; n < opcoes.length; n++) {
      const i = (inicio + n) % opcoes.length;
      const o = opcoes[i]!;
      if (!o.disabled && normal(o.rotulo).startsWith(prefixo)) return i;
    }
    return -1;
  }

  function aoTeclarNoBotao(e: ReactKeyboardEvent<HTMLButtonElement>) {
    if (disabled) return;
    const k = e.key;
    if (k === 'Enter' || k === ' ' || k === 'ArrowDown' || k === 'ArrowUp' || k === 'Home' || k === 'End') {
      e.preventDefault();
      abrir();
      return;
    }
    if (k.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey && !multiple) {
      // Como o `<select>` fechado: digitar troca o valor sem abrir.
      const atual = opcoes.findIndex((o) => o.value === escolhidos[0]);
      const i = saltarPorTexto(k, atual);
      if (i >= 0) escolher(opcoes[i]!);
    }
  }

  function aoTeclarNaLista(e: ReactKeyboardEvent<HTMLDivElement>) {
    const k = e.key;
    if (k === 'Escape') {
      e.preventDefault();
      e.stopPropagation(); // não fecha o modal que contém o campo
      setAberto(false);
      botaoRef.current?.focus();
      return;
    }
    if (k === 'Tab') {
      // Sai do controle: fecha e deixa o foco seguir do botão.
      setAberto(false);
      botaoRef.current?.focus();
      return;
    }
    if (k === 'ArrowDown') { e.preventDefault(); setAtivo((i) => proximoHabilitado(i, 1)); return; }
    if (k === 'ArrowUp') { e.preventDefault(); setAtivo((i) => proximoHabilitado(i, -1)); return; }
    if (k === 'Home') { e.preventDefault(); const i = primeiroHabilitado(); if (i >= 0) setAtivo(i); return; }
    if (k === 'End') { e.preventDefault(); const i = ultimoHabilitado(); if (i >= 0) setAtivo(i); return; }
    if (k === 'PageDown') { e.preventDefault(); setAtivo((i) => { let j = i; for (let n = 0; n < 7; n++) j = proximoHabilitado(j, 1); return j; }); return; }
    if (k === 'PageUp') { e.preventDefault(); setAtivo((i) => { let j = i; for (let n = 0; n < 7; n++) j = proximoHabilitado(j, -1); return j; }); return; }
    if (k === 'Enter' || k === ' ') {
      e.preventDefault();
      const o = opcoes[ativo];
      if (o) escolher(o);
      return;
    }
    if (k.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
      e.preventDefault();
      const i = saltarPorTexto(k, ativo);
      if (i >= 0) setAtivo(i);
    }
  }

  const rotuloAtual = (() => {
    const marcadas = opcoes.filter((o) => escolhidos.includes(o.value));
    if (marcadas.length === 0) return placeholder ?? '';
    if (!multiple) return marcadas[0]!.rotulo;
    if (marcadas.length <= 2) return marcadas.map((o) => o.rotulo).join(', ');
    return `${marcadas.length} selecionados`;
  })();
  const vazio = rotuloAtual === '' || (placeholder !== undefined && rotuloAtual === placeholder);

  /* Rótulo acessível do botão: o `<label for>` do consumidor aponta para o
     botão (é ele quem recebe `id`), o nativo escondido só carrega o valor. */
  const idDaLista = `${meuId}-lista`;

  // Agrupa as opções por optgroup preservando a ordem.
  const grupos: { nome?: string; itens: OpcaoLida[] }[] = [];
  for (const o of opcoes) {
    const ultimo = grupos[grupos.length - 1];
    if (ultimo && ultimo.nome === o.grupo) ultimo.itens.push(o);
    else grupos.push({ nome: o.grupo, itens: [o] });
  }

  return (
    <div className={`seletor${disabled ? ' seletor--desabilitado' : ''}${className ? ' ' + className : ''}`} style={style}>
      {/* Nativo, invisível, ainda no formulário. `tabIndex={-1}` para não roubar
          o Tab; `aria-hidden` porque quem fala com o leitor de tela é o botão. */}
      <select
        ref={nativoRef}
        className="seletor__nativo"
        tabIndex={-1}
        aria-hidden="true"
        multiple={multiple}
        disabled={disabled}
        value={multiple ? escolhidos : (escolhidos[0] ?? '')}
        onChange={(e) => { onChange?.(e); }}
        onFocus={() => botaoRef.current?.focus()}
        {...resto}
      >
        {children}
      </select>
      <button
        ref={botaoRef}
        id={meuId}
        type="button"
        className={`seletor__botao select-custom__botao${vazio ? ' seletor__botao--vazio' : ''}`}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={aberto}
        aria-controls={aberto ? idDaLista : undefined}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledby}
        aria-describedby={ariaDescribedby}
        aria-invalid={ariaInvalid}
        aria-required={resto.required || undefined}
        onClick={() => (aberto ? setAberto(false) : abrir())}
        onKeyDown={aoTeclarNoBotao}
        onBlur={(e) => onBlur?.(e as unknown as ReactFocusEvent<HTMLSelectElement>)}
        onFocus={(e) => onFocus?.(e as unknown as ReactFocusEvent<HTMLSelectElement>)}
      >
        <span className="select-custom__valor">{rotuloAtual || ' '}</span>
        <Icone.ChevronBaixo tamanho={14} className="select-custom__seta" aria-hidden="true" />
      </button>
      {aberto && pos && createPortal(
        <div
          ref={listaRef}
          id={idDaLista}
          className={`seletor__lista select-custom__lista${pos.flip ? ' select-custom__lista--cima' : ''}`}
          role="listbox"
          data-theme="light"
          tabIndex={-1}
          aria-labelledby={ariaLabelledby}
          aria-label={ariaLabel ?? nomeDaLista}
          aria-multiselectable={multiple || undefined}
          aria-activedescendant={opcoes[ativo] ? `${idDaLista}-${ativo}` : undefined}
          onKeyDown={aoTeclarNaLista}
          style={estiloDoPainel(pos)}
        >
          {opcoes.length === 0 && <div className="seletor__vazio">Sem opções</div>}
          {grupos.map((g, gi) => (
            <div key={gi} role={g.nome ? 'group' : undefined} aria-label={g.nome} className="seletor__grupo">
              {g.nome && <div className="seletor__grupo-titulo" aria-hidden="true">{g.nome}</div>}
              {g.itens.map((o) => {
                const marcada = escolhidos.includes(o.value);
                return (
                  <div
                    key={`${o.i}-${o.value}`}
                    id={`${idDaLista}-${o.i}`}
                    data-i={o.i}
                    role="option"
                    aria-selected={marcada}
                    aria-disabled={o.disabled || undefined}
                    className={`seletor__opcao select-custom__opcao${o.i === ativo ? ' seletor__opcao--ativa' : ''}${o.disabled ? ' seletor__opcao--desabilitada' : ''}`}
                    onPointerMove={() => { if (!o.disabled && ativo !== o.i) setAtivo(o.i); }}
                    onPointerDown={(e) => e.preventDefault()}
                    onClick={() => escolher(o)}
                  >
                    {multiple && <span className={`seletor__marca${marcada ? ' seletor__marca--on' : ''}`} aria-hidden="true" />}
                    <span className="seletor__opcao-rotulo">{o.rotulo}</span>
                  </div>
                );
              })}
            </div>
          ))}
        </div>,
        document.body,
      )}
    </div>
  );
}

/* ======================================================================
 * CampoDeData
 * ====================================================================== */

export type TipoDeData = 'date' | 'datetime-local' | 'month' | 'time';

export type CampoDeDataProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> & {
  type: TipoDeData;
  /** Passo em minutos da lista de horários (padrão 15; `step` em segundos do HTML, se vier, prevalece). */
  passoMinutos?: number;
  /** Ano mínimo e máximo do seletor de ano quando `min`/`max` não informam. */
  anos?: [number, number];
};

const DIAS = ['D', 'S', 'T', 'Q', 'Q', 'S', 'S'];
const DIAS_LONGOS = ['domingo', 'segunda-feira', 'terça-feira', 'quarta-feira', 'quinta-feira', 'sexta-feira', 'sábado'];
const MESES = ['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho', 'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'];

const p2 = (n: number) => String(n).padStart(2, '0');

type Partes = { ano?: number; mes?: number; dia?: number; hora?: number; minuto?: number };

/** Lê a string ISO local do tipo, sem `Date`, para não mexer em fuso. */
function lerValor(tipo: TipoDeData, v: string): Partes {
  if (!v) return {};
  if (tipo === 'time') {
    const m = /^(\d{2}):(\d{2})/.exec(v);
    return m ? { hora: +m[1]!, minuto: +m[2]! } : {};
  }
  if (tipo === 'month') {
    const m = /^(\d{4})-(\d{2})$/.exec(v);
    return m ? { ano: +m[1]!, mes: +m[2]! } : {};
  }
  const m = /^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2}))?/.exec(v);
  if (!m) return {};
  return { ano: +m[1]!, mes: +m[2]!, dia: +m[3]!, hora: m[4] ? +m[4] : undefined, minuto: m[5] ? +m[5] : undefined };
}

/** Monta a string ISO local do tipo a partir das partes. Nada de `Date`. */
function montar(tipo: TipoDeData, p: Partes): string {
  if (tipo === 'time') return p.hora == null || p.minuto == null ? '' : `${p2(p.hora)}:${p2(p.minuto)}`;
  if (tipo === 'month') return p.ano == null || p.mes == null ? '' : `${p.ano}-${p2(p.mes)}`;
  if (p.ano == null || p.mes == null || p.dia == null) return '';
  const data = `${p.ano}-${p2(p.mes)}-${p2(p.dia)}`;
  if (tipo === 'date') return data;
  return `${data}T${p2(p.hora ?? 0)}:${p2(p.minuto ?? 0)}`;
}

/** Chave comparável (string ISO) para `min`/`max` no mesmo tipo. */
function chaveDeData(p: Partes): string {
  return p.ano == null || p.mes == null || p.dia == null ? '' : `${p.ano}-${p2(p.mes)}-${p2(p.dia)}`;
}
function chaveDeMes(p: Partes): string {
  return p.ano == null || p.mes == null ? '' : `${p.ano}-${p2(p.mes)}`;
}

function diasNoMes(ano: number, mes: number): number {
  return new Date(ano, mes, 0).getDate(); // só para contar dias; nunca sai para o valor
}
function diaDaSemanaDoPrimeiro(ano: number, mes: number): number {
  return new Date(ano, mes - 1, 1).getDay();
}
function hoje(): Partes {
  const d = new Date();
  return { ano: d.getFullYear(), mes: d.getMonth() + 1, dia: d.getDate(), hora: d.getHours(), minuto: d.getMinutes() };
}

function porExtenso(tipo: TipoDeData, p: Partes): string {
  if (tipo === 'time') return p.hora == null ? '' : `${p2(p.hora)}:${p2(p.minuto ?? 0)}`;
  if (tipo === 'month') return p.ano == null || p.mes == null ? '' : `${MESES[p.mes - 1]} de ${p.ano}`;
  if (p.ano == null || p.mes == null || p.dia == null) return '';
  const data = `${p2(p.dia)}/${p2(p.mes)}/${p.ano}`;
  return tipo === 'date' ? data : `${data} ${p2(p.hora ?? 0)}:${p2(p.minuto ?? 0)}`;
}

/**
 * Campo de data, data e hora, mês ou hora, com painel desenhado pelo DS.
 * Nunca abre o seletor nativo: o `<input>` real fica escondido e recebe o
 * valor ISO local; `required`, `min`, `max`, `name` e `form` continuam
 * valendo para a validação do formulário.
 *
 * Teclado no botão: Enter, Espaço, setas abrem. No calendário: setas andam
 * um dia ou uma semana, PageUp/PageDown trocam o mês (com Shift, o ano),
 * Home/End vão ao começo e fim da semana, Enter escolhe, Esc fecha.
 */
export function CampoDeData(props: CampoDeDataProps) {
  const {
    type: tipo, id, className = '', value, defaultValue, onChange, disabled, readOnly, min, max, step,
    placeholder, passoMinutos, anos, onBlur, onFocus, style,
    'aria-label': ariaLabel, 'aria-labelledby': ariaLabelledby, 'aria-describedby': ariaDescribedby,
    'aria-invalid': ariaInvalid, ...resto
  } = props;
  const gerado = useId();
  const meuId = id ?? gerado;
  const nativoRef = useRef<HTMLInputElement>(null);
  const botaoRef = useRef<HTMLButtonElement>(null);
  const painelRef = useRef<HTMLDivElement>(null);
  const gradeRef = useRef<HTMLDivElement>(null);
  const refs = useMemo(() => [botaoRef, painelRef], []);

  const controlado = value !== undefined;
  const [interno, setInterno] = useState<string>(defaultValue == null ? '' : String(defaultValue));
  const valor = controlado ? String(value ?? '') : interno;
  const partes = lerValor(tipo, valor);
  useEffect(() => {
    const el = nativoRef.current;
    const form = el?.form;
    if (!el || !form) return;
    const restaurar = () => queueMicrotask(() => {
      const novo = String(controlado ? value ?? '' : defaultValue ?? '');
      if (!controlado) setInterno(novo);
      el.value = novo;
    });
    form.addEventListener('reset', restaurar);
    return () => form.removeEventListener('reset', restaurar);
  }, [controlado, value, defaultValue]);


  const minP = lerValor(tipo, min == null ? '' : String(min));
  const maxP = lerValor(tipo, max == null ? '' : String(max));

  const [aberto, setAberto] = useState(false);
  const temCalendario = tipo !== 'time';
  const temHora = tipo === 'time' || tipo === 'datetime-local';
  const alturaPrevista = tipo === 'time' ? 300 : tipo === 'month' ? 300 : temHora ? 420 : 360;
  const pos = usePainelFlutuante(aberto, botaoRef, alturaPrevista, tipo === 'time' ? 0 : tipo === 'datetime-local' ? 480 : 368);
  const fechar = useMemo(() => () => setAberto(false), []);
  useFecharFora(aberto, refs, fechar);

  /* Cursor do calendário: o mês exibido e o dia com foco. */
  const h = hoje();
  const [cursor, setCursor] = useState<{ ano: number; mes: number; dia: number }>({ ano: h.ano!, mes: h.mes!, dia: h.dia! });
  const [vistaDeAno, setVistaDeAno] = useState(false);

  function abrir() {
    if (disabled || readOnly) return;
    const base = partes.ano != null && partes.mes != null ? partes : h;
    let dia = partes.dia ?? h.dia!;
    dia = Math.min(dia, diasNoMes(base.ano!, base.mes!));
    setCursor({ ano: base.ano!, mes: base.mes!, dia });
    setVistaDeAno(false);
    setAberto(true);
  }

  useEffect(() => {
    if (!aberto || !pos) return; // `pos`: o painel só existe no DOM depois do layout effect
    // Foco vai para o dia (ou para o horário quando não há calendário).
    const alvo = temCalendario
      ? gradeRef.current?.querySelector<HTMLElement>('[data-foco="1"]')
      : painelRef.current?.querySelector<HTMLElement>('[aria-selected="true"]') ?? painelRef.current?.querySelector<HTMLElement>('[role="option"]');
    (alvo ?? painelRef.current)?.focus();
  }, [aberto, pos, temCalendario]);

  useEffect(() => {
    if (!aberto || !temCalendario || vistaDeAno) return;
    gradeRef.current?.querySelector<HTMLElement>('[data-foco="1"]')?.focus();
  }, [aberto, temCalendario, vistaDeAno, cursor]);

  function aplicar(novo: string) {
    if (!controlado) setInterno(novo);
    if (nativoRef.current) escreverNoNativo(nativoRef.current, novo);
  }

  /* Limites por tipo. */
  const foraDoLimite = (p: Partes): boolean => {
    if (tipo === 'month') {
      const k = chaveDeMes(p);
      return (chaveDeMes(minP) !== '' && k < chaveDeMes(minP)) || (chaveDeMes(maxP) !== '' && k > chaveDeMes(maxP));
    }
    const k = chaveDeData(p);
    return (chaveDeData(minP) !== '' && k < chaveDeData(minP)) || (chaveDeData(maxP) !== '' && k > chaveDeData(maxP));
  };
  const horaForaDoLimite = (hora: number, minuto: number): boolean => {
    if (tipo === 'time') {
      const k = `${p2(hora)}:${p2(minuto)}`;
      const mn = minP.hora == null ? '' : `${p2(minP.hora)}:${p2(minP.minuto ?? 0)}`;
      const mx = maxP.hora == null ? '' : `${p2(maxP.hora)}:${p2(maxP.minuto ?? 0)}`;
      return (mn !== '' && k < mn) || (mx !== '' && k > mx);
    }
    // datetime-local: só restringe a hora quando o dia é o do limite.
    const k = montar('datetime-local', { ...partes, ano: cursor.ano, mes: cursor.mes, dia: partes.dia ?? cursor.dia, hora, minuto });
    const mn = montar('datetime-local', minP);
    const mx = montar('datetime-local', maxP);
    return (mn !== '' && k < mn) || (mx !== '' && k > mx);
  };

  function escolherDia(dia: number) {
    const p: Partes = { ano: cursor.ano, mes: cursor.mes, dia };
    if (foraDoLimite(p)) return;
    if (tipo === 'datetime-local') {
      aplicar(montar(tipo, { ...p, hora: partes.hora ?? 0, minuto: partes.minuto ?? 0 }));
      setCursor({ ano: cursor.ano, mes: cursor.mes, dia });
      return; // fica aberto para escolher a hora
    }
    aplicar(montar(tipo, p));
    setAberto(false);
    botaoRef.current?.focus();
  }
  function escolherMes(mes: number) {
    const p: Partes = { ano: cursor.ano, mes };
    if (foraDoLimite(p)) return;
    aplicar(montar('month', p));
    setAberto(false);
    botaoRef.current?.focus();
  }
  function escolherHora(hora: number, minuto: number) {
    if (horaForaDoLimite(hora, minuto)) return;
    if (tipo === 'time') {
      aplicar(montar('time', { hora, minuto }));
      setAberto(false);
      botaoRef.current?.focus();
      return;
    }
    const base = partes.dia != null ? partes : { ano: cursor.ano, mes: cursor.mes, dia: cursor.dia };
    aplicar(montar('datetime-local', { ...base, hora, minuto }));
    setAberto(false);
    botaoRef.current?.focus();
  }
  function escolherHoje() {
    const n = hoje();
    if (tipo === 'time') { escolherHora(n.hora!, n.minuto!); return; }
    if (tipo === 'month') { if (!foraDoLimite(n)) { aplicar(montar('month', n)); setAberto(false); botaoRef.current?.focus(); } return; }
    if (foraDoLimite(n)) return;
    aplicar(montar(tipo, tipo === 'date' ? n : { ...n, hora: partes.hora ?? n.hora, minuto: partes.minuto ?? n.minuto }));
    setAberto(false);
    botaoRef.current?.focus();
  }
  function limpar() {
    aplicar('');
    setAberto(false);
    botaoRef.current?.focus();
  }

  function moverCursor(dias: number) {
    // Anda no calendário usando aritmética de dias; `Date` só como contador.
    const d = new Date(cursor.ano, cursor.mes - 1, cursor.dia + dias);
    setCursor({ ano: d.getFullYear(), mes: d.getMonth() + 1, dia: d.getDate() });
  }
  function moverMes(delta: number, anosInteiros = false) {
    let ano = cursor.ano + (anosInteiros ? delta : 0);
    let mes = cursor.mes + (anosInteiros ? 0 : delta);
    while (mes < 1) { mes += 12; ano -= 1; }
    while (mes > 12) { mes -= 12; ano += 1; }
    setCursor({ ano, mes, dia: Math.min(cursor.dia, diasNoMes(ano, mes)) });
  }

  function aoTeclarNoPainel(e: ReactKeyboardEvent<HTMLDivElement>) {
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      setAberto(false);
      botaoRef.current?.focus();
    }
  }
  function aoTeclarNaGrade(e: ReactKeyboardEvent<HTMLDivElement>) {
    const k = e.key;
    if (k === 'ArrowLeft') { e.preventDefault(); moverCursor(-1); }
    else if (k === 'ArrowRight') { e.preventDefault(); moverCursor(1); }
    else if (k === 'ArrowUp') { e.preventDefault(); moverCursor(-7); }
    else if (k === 'ArrowDown') { e.preventDefault(); moverCursor(7); }
    else if (k === 'Home') { e.preventDefault(); moverCursor(-new Date(cursor.ano, cursor.mes - 1, cursor.dia).getDay()); }
    else if (k === 'End') { e.preventDefault(); moverCursor(6 - new Date(cursor.ano, cursor.mes - 1, cursor.dia).getDay()); }
    else if (k === 'PageUp') { e.preventDefault(); moverMes(-1, e.shiftKey); }
    else if (k === 'PageDown') { e.preventDefault(); moverMes(1, e.shiftKey); }
    else if (k === 'Enter' || k === ' ') { e.preventDefault(); escolherDia(cursor.dia); }
  }

  function aoTeclarNoBotao(e: ReactKeyboardEvent<HTMLButtonElement>) {
    if (disabled || readOnly) return;
    if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      abrir();
    } else if ((e.key === 'Backspace' || e.key === 'Delete') && valor) {
      e.preventDefault();
      aplicar('');
    }
  }

  /* Lista de horários. */
  const passo = step != null && Number(step) > 0 ? Math.max(1, Math.round(Number(step) / 60)) : (passoMinutos ?? 15);
  const horarios = useMemo(() => {
    const lista: { hora: number; minuto: number }[] = [];
    for (let m = 0; m < 24 * 60; m += passo) lista.push({ hora: Math.floor(m / 60), minuto: m % 60 });
    // O valor atual entra na lista mesmo fora do passo (ex.: 09:07 com passo 15).
    if (partes.hora != null && partes.minuto != null && !lista.some((x) => x.hora === partes.hora && x.minuto === partes.minuto)) {
      lista.push({ hora: partes.hora, minuto: partes.minuto });
      lista.sort((a, b) => a.hora * 60 + a.minuto - (b.hora * 60 + b.minuto));
    }
    return lista;
  }, [passo, partes.hora, partes.minuto]);

  useEffect(() => {
    if (!aberto || !pos || !temHora) return;
    painelRef.current?.querySelector<HTMLElement>('.seletor-data__hora [aria-selected="true"]')?.scrollIntoView({ block: 'center' });
  }, [aberto, pos, temHora]);

  function aoTeclarNasHoras(e: ReactKeyboardEvent<HTMLDivElement>) {
    const itens = Array.from(e.currentTarget.querySelectorAll<HTMLElement>('[role="option"]'));
    const i = itens.indexOf(document.activeElement as HTMLElement);
    const focar = (j: number) => itens[Math.max(0, Math.min(itens.length - 1, j))]?.focus();
    if (e.key === 'ArrowDown') { e.preventDefault(); focar(i + 1); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); focar(i - 1); }
    else if (e.key === 'Home') { e.preventDefault(); focar(0); }
    else if (e.key === 'End') { e.preventDefault(); focar(itens.length - 1); }
    else if (e.key === 'PageDown') { e.preventDefault(); focar(i + Math.round(60 / passo)); }
    else if (e.key === 'PageUp') { e.preventDefault(); focar(i - Math.round(60 / passo)); }
  }

  /* Seletor de ano: usa min/max quando existem, senão `anos` ou 100 anos ao redor de hoje. */
  const anoInicial = minP.ano ?? anos?.[0] ?? h.ano! - 80;
  const anoFinal = maxP.ano ?? anos?.[1] ?? h.ano! + 20;
  const listaDeAnos = useMemo(() => {
    const l: number[] = [];
    for (let a = Math.min(anoInicial, cursor.ano); a <= Math.max(anoFinal, cursor.ano); a++) l.push(a);
    return l;
  }, [anoInicial, anoFinal, cursor.ano]);

  const idDoPainel = `${meuId}-painel`;
  const texto = porExtenso(tipo, partes);
  const placeholderPadrao = tipo === 'time' ? 'hh:mm' : tipo === 'month' ? 'mm/aaaa' : tipo === 'date' ? 'dd/mm/aaaa' : 'dd/mm/aaaa hh:mm';

  /* Grade do mês. */
  const primeiro = diaDaSemanaDoPrimeiro(cursor.ano, cursor.mes);
  const total = diasNoMes(cursor.ano, cursor.mes);
  const celulas: (number | null)[] = [];
  for (let i = 0; i < primeiro; i++) celulas.push(null);
  for (let d = 1; d <= total; d++) celulas.push(d);
  while (celulas.length % 7 !== 0) celulas.push(null);
  const eHoje = (d: number) => cursor.ano === h.ano && cursor.mes === h.mes && d === h.dia;
  const eEscolhido = (d: number) => partes.ano === cursor.ano && partes.mes === cursor.mes && partes.dia === d;

  return (
    <div className={`seletor seletor-data${disabled ? ' seletor--desabilitado' : ''}${className ? ' ' + className : ''}`} style={style}>
      <input
        ref={nativoRef}
        type={tipo}
        className="seletor__nativo"
        tabIndex={-1}
        aria-hidden="true"
        disabled={disabled}
        readOnly={readOnly}
        min={min}
        max={max}
        step={step}
        value={valor}
        onChange={(e) => { onChange?.(e); }}
        onFocus={() => botaoRef.current?.focus()}
        {...resto}
      />
      <button
        ref={botaoRef}
        id={meuId}
        type="button"
        className={`seletor__botao select-custom__botao${texto ? '' : ' seletor__botao--vazio'}`}
        disabled={disabled}
        aria-haspopup="dialog"
        aria-expanded={aberto}
        aria-controls={aberto ? idDoPainel : undefined}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledby}
        aria-describedby={ariaDescribedby}
        aria-invalid={ariaInvalid}
        aria-required={resto.required || undefined}
        aria-readonly={readOnly || undefined}
        onClick={() => (aberto ? setAberto(false) : abrir())}
        onKeyDown={aoTeclarNoBotao}
        onBlur={(e) => onBlur?.(e as unknown as ReactFocusEvent<HTMLInputElement>)}
        onFocus={(e) => onFocus?.(e as unknown as ReactFocusEvent<HTMLInputElement>)}
      >
        <span className="select-custom__valor">{texto || placeholder || placeholderPadrao}</span>
        {tipo === 'time'
          ? <Icone.Cronometro tamanho={16} className="select-custom__seta seletor-data__icone" aria-hidden="true" />
          : <Icone.Calendario tamanho={16} className="select-custom__seta seletor-data__icone" aria-hidden="true" />}
      </button>
      {aberto && pos && createPortal(
        <div
          ref={painelRef}
          id={idDoPainel}
          role="dialog"
          data-theme="light"
          aria-modal="false"
          aria-label={ariaLabel ?? (tipo === 'time' ? 'Escolher horário' : 'Escolher data')}
          className={`seletor__lista select-custom__lista seletor-data__painel seletor-data__painel--${tipo}${pos.flip ? ' select-custom__lista--cima' : ''}`}
          tabIndex={-1}
          onKeyDown={aoTeclarNoPainel}
          style={estiloDoPainel(pos)}
        >
          {temCalendario && (
            <div className="seletor-data__calendario">
              <div className="seletor-data__cabecalho">
                <button type="button" className="seletor-data__nav" aria-label={tipo === 'month' ? 'Ano anterior' : 'Mês anterior'} onClick={() => moverMes(-1, tipo === 'month')}>
                  <Icone.SetaEsquerda tamanho={16} aria-hidden="true" />
                </button>
                <button
                  type="button"
                  className="seletor-data__titulo"
                  aria-expanded={vistaDeAno}
                  aria-label={vistaDeAno ? 'Voltar ao calendário' : 'Escolher mês e ano'}
                  onClick={() => setVistaDeAno((v) => !v)}
                >
                  {tipo === 'month' ? cursor.ano : `${MESES[cursor.mes - 1]} de ${cursor.ano}`}
                  <Icone.ChevronBaixo tamanho={12} aria-hidden="true" />
                </button>
                <button type="button" className="seletor-data__nav" aria-label={tipo === 'month' ? 'Próximo ano' : 'Próximo mês'} onClick={() => moverMes(1, tipo === 'month')}>
                  <Icone.SetaDireita tamanho={16} aria-hidden="true" />
                </button>
              </div>

              {vistaDeAno ? (
                <div className="seletor-data__ano-mes">
                  <div className="seletor-data__ano">
                    <SelectDoSuite
                      value={String(cursor.ano)}
                      onChange={(e) => setCursor((c) => ({ ...c, ano: Number(e.target.value), dia: Math.min(c.dia, diasNoMes(Number(e.target.value), c.mes)) }))}
                      aria-label="Ano"
                    >
                      {listaDeAnos.map((a) => <option key={a} value={a}>{a}</option>)}
                    </SelectDoSuite>
                  </div>
                  <div className="seletor-data__meses" role="group" aria-label="Mês">
                    {MESES.map((nome, i) => {
                      const mes = i + 1;
                      const fora = tipo === 'month' ? foraDoLimite({ ano: cursor.ano, mes }) : false;
                      const ativo = mes === cursor.mes;
                      return (
                        <button
                          key={nome}
                          type="button"
                          className={`seletor-data__mes${ativo ? ' seletor-data__mes--ativo' : ''}`}
                          aria-pressed={ativo}
                          disabled={fora}
                          onClick={() => { setCursor((c) => ({ ...c, mes, dia: Math.min(c.dia, diasNoMes(c.ano, mes)) })); if (tipo !== 'month') setVistaDeAno(false); }}
                        >
                          {nome.slice(0, 3)}
                        </button>
                      );
                    })}
                  </div>
                  {tipo === 'month' && (
                    <button type="button" className="seletor-data__acao seletor-data__acao--primaria" onClick={() => escolherMes(cursor.mes)}>
                      Usar {MESES[cursor.mes - 1]} de {cursor.ano}
                    </button>
                  )}
                </div>
              ) : tipo === 'month' ? (
                <div className="seletor-data__meses" role="group" aria-label="Mês">
                  {MESES.map((nome, i) => {
                    const mes = i + 1;
                    const fora = foraDoLimite({ ano: cursor.ano, mes });
                    const escolhido = partes.ano === cursor.ano && partes.mes === mes;
                    return (
                      <button
                        key={nome}
                        type="button"
                        className={`seletor-data__mes${escolhido ? ' seletor-data__mes--ativo' : ''}`}
                        aria-pressed={escolhido}
                        disabled={fora}
                        onClick={() => escolherMes(mes)}
                      >
                        {nome.slice(0, 3)}
                      </button>
                    );
                  })}
                </div>
              ) : (
                <div
                  ref={gradeRef}
                  role="grid"
                  aria-label={`${MESES[cursor.mes - 1]} de ${cursor.ano}`}
                  className="seletor-data__grade"
                  onKeyDown={aoTeclarNaGrade}
                >
                  <div role="row" className="seletor-data__semana seletor-data__semana--titulos">
                    {DIAS.map((d, i) => <div key={i} role="columnheader" aria-label={DIAS_LONGOS[i]} className="seletor-data__dia-titulo">{d}</div>)}
                  </div>
                  {Array.from({ length: celulas.length / 7 }, (_, s) => (
                    <div key={s} role="row" className="seletor-data__semana">
                      {celulas.slice(s * 7, s * 7 + 7).map((d, i) => {
                        if (d == null) return <div key={i} role="gridcell" className="seletor-data__dia seletor-data__dia--vazio" aria-hidden="true" />;
                        const fora = foraDoLimite({ ano: cursor.ano, mes: cursor.mes, dia: d });
                        const focado = d === cursor.dia;
                        return (
                          <div key={i} role="gridcell" className="seletor-data__celula">
                            <button
                              type="button"
                              className={`seletor-data__dia${eEscolhido(d) ? ' seletor-data__dia--escolhido' : ''}${eHoje(d) ? ' seletor-data__dia--hoje' : ''}`}
                              tabIndex={focado ? 0 : -1}
                              data-foco={focado ? '1' : undefined}
                              aria-pressed={eEscolhido(d)}
                              aria-current={eHoje(d) ? 'date' : undefined}
                              aria-label={`${d} de ${MESES[cursor.mes - 1]} de ${cursor.ano}, ${DIAS_LONGOS[(primeiro + d - 1) % 7]}`}
                              disabled={fora}
                              onClick={() => { setCursor((c) => ({ ...c, dia: d })); escolherDia(d); }}
                            >
                              {d}
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {temHora && (
            <div className="seletor-data__hora">
              <div className="seletor-data__hora-titulo" id={`${idDoPainel}-hora`}>Horário</div>
              <div className="seletor-data__horas" role="listbox" aria-labelledby={`${idDoPainel}-hora`} onKeyDown={aoTeclarNasHoras}>
                {horarios.map((x) => {
                  const marcada = partes.hora === x.hora && partes.minuto === x.minuto;
                  const fora = horaForaDoLimite(x.hora, x.minuto);
                  return (
                    <button
                      key={`${x.hora}-${x.minuto}`}
                      type="button"
                      role="option"
                      aria-selected={marcada}
                      className={`seletor__opcao select-custom__opcao seletor-data__opcao-hora${marcada ? ' seletor__opcao--ativa' : ''}`}
                      tabIndex={marcada || (partes.hora == null && x.hora === 0 && x.minuto === 0) ? 0 : -1}
                      disabled={fora}
                      onClick={() => escolherHora(x.hora, x.minuto)}
                    >
                      {p2(x.hora)}:{p2(x.minuto)}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div className="seletor-data__rodape">
            <button type="button" className="seletor-data__acao" onClick={escolherHoje}>{tipo === 'time' ? 'Agora' : 'Hoje'}</button>
            <button type="button" className="seletor-data__acao" onClick={limpar} disabled={!valor}>Limpar</button>
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}

/** Tipos de `<input>` que `CampoDeData` cobre. `Field` usa para decidir. */
export function eTipoDeData(t: unknown): t is TipoDeData {
  return t === 'date' || t === 'datetime-local' || t === 'month' || t === 'time';
}
